import { Fragment, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import styles from "../ContractSearch.styles";
import { parseAnswer } from "../ContractSearchHelper";

interface AnswerMarkdownProps {
  answer: string;
  /**
   * What goes beside a contract heading (the compare pill or hint -- the frames draw it beside the bold file
   * name), or `null` for a heading that names no matched contract. Called with the heading's text and the id of
   * the heading element, which describes the control.
   */
  headingExtra?: (headingText: string, headingId: string) => ReactNode;
  /** Prefix for the heading ids: unique per AI turn. */
  idPrefix?: string;
}

/**
 * Renders the AI's light-Markdown answer: paragraphs, `**bold**` runs and one
 * bold heading per contract. Everything is emitted as React text nodes, so
 * nothing in the answer can ever be interpreted as HTML.
 */
const AnswerMarkdown = ({ answer, headingExtra, idPrefix = "answer" }: AnswerMarkdownProps) => {
  const blocks = parseAnswer(answer);
  if (!blocks.length) return null;

  return (
    <Box sx={styles.answerBlocks}>
      {blocks.map((block, index) => {
        if (block.kind === "heading") {
          const text = block.runs.map((run) => run.text).join("");
          const id = `${idPrefix}-heading-${index}`;
          const extra = headingExtra?.(text, id);
          if (!extra) {
            return (
              <Typography component="h4" id={id} key={index} sx={styles.answerHeading}>
                {text}
              </Typography>
            );
          }
          return (
            <Box key={index} sx={styles.answerHeadingRow}>
              <Typography component="h4" id={id} sx={styles.answerHeadingInRow}>
                {text}
              </Typography>
              <Box sx={styles.answerCompareEntry}>{extra}</Box>
            </Box>
          );
        }
        return (
          <Typography component="p" key={index} sx={styles.answerBody}>
            {block.runs.map((run, runIndex) => (
              <Fragment key={runIndex}>
                {run.bold ? <strong>{run.text}</strong> : run.text}
              </Fragment>
            ))}
          </Typography>
        );
      })}
    </Box>
  );
};

export default AnswerMarkdown;
